/**
 * O som do sino (spec 048, 2.10; Vitor, 12/10: "so preciso que coloque som naquelas notificacoes, se nao e inutil").
 *
 * O som e gerado aqui (Web Audio), sem arquivo pra baixar. O navegador so deixa tocar depois que a pessoa mexeu na
 * pagina uma vez (um clique ou uma tecla): ate la o contexto de audio fica suspenso, e o painel mostra "Ativar som".
 * Quais avisos tocam e decisao do backend (`som` em cada item do sino), nao desta tela.
 */
import { useEffect, useRef, useState } from "react";

let contexto: AudioContext | null = null;
const ouvintes = new Set<(ativo: boolean) => void>();

function ativar() {
  try {
    contexto ??= new AudioContext();
    void contexto.resume().then(() => ouvintes.forEach((f) => f(contexto?.state === "running")));
  } catch {
    // Navegador sem Web Audio: o sino continua funcionando, so sem som.
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", ativar, { passive: true });
  window.addEventListener("keydown", ativar);
}

/** Dois toques curtos, do agudo pro mais agudo: da pra ouvir com a aba em segundo plano sem assustar ninguem. */
export function tocarSino(): boolean {
  if (!contexto || contexto.state !== "running") return false;
  const agora = contexto.currentTime;
  for (const [i, freq] of [880, 1320].entries()) {
    const osc = contexto.createOscillator();
    const volume = contexto.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const inicio = agora + i * 0.18;
    volume.gain.setValueAtTime(0.0001, inicio);
    volume.gain.exponentialRampToValueAtTime(0.25, inicio + 0.02);
    volume.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.35);
    osc.connect(volume).connect(contexto.destination);
    osc.start(inicio);
    osc.stop(inicio + 0.4);
  }
  return true;
}

/** Se o navegador ja deixa tocar (a pessoa mexeu na pagina). */
export function useSomAtivo(): boolean {
  const [ativo, setAtivo] = useState(() => contexto?.state === "running");
  useEffect(() => {
    ouvintes.add(setAtivo);
    return () => void ouvintes.delete(setAtivo);
  }, []);
  return ativo;
}

/**
 * Toca quando aparece no sino um item com `som` que esta tela ainda nao tinha visto. A primeira leitura so aprende o que
 * ja estava la: abrir o painel com dez pendencias antigas nao dispara dez toques.
 */
export function useSomDoSino(itens: { id: string; som?: boolean }[] | undefined): void {
  const vistos = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!itens) return;
    if (!vistos.current) {
      vistos.current = new Set(itens.map((i) => i.id));
      return;
    }
    const novos = itens.filter((i) => !vistos.current!.has(i.id));
    novos.forEach((i) => vistos.current!.add(i.id));
    if (novos.some((i) => i.som)) tocarSino();
  }, [itens]);
}
